import { skipToken } from '@apollo/client/react'
import {
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import {
  type FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import IcBaselineChevronLeft from '~icons/ic/baseline-chevron-left'
import IcBaselineChevronRight from '~icons/ic/baseline-chevron-right'
import IcBaselineClear from '~icons/ic/baseline-clear'
import IcBaselineSearch from '~icons/ic/baseline-search'
import { CuratedArtworkCard } from 'src/components/Artwork/CuratedArtworkCard'
import ArtworkDetailDialog from 'src/components/ArtworkDetail/ArtworkDetailDialog'
import StatefulArtworkGrid from 'src/components/ArtworkList/StatefulArtworkGrid'
import {
  type ArtworkFeedResult,
  useListArtworkFeed,
} from 'src/components/ArtworkList/useArtworkFeed'
import { CustomHeroTitleWithDescription } from 'src/components/Common/SiteName'
import BaseModelListInfiniteScroll from 'src/components/GenerationModel/GenerationModelList'
import ModelGrid from 'src/components/GenerationModel/GenerationModelList/ModelGrid'
import { useGenerationModelFeed } from 'src/components/GenerationModel/GenerationModelList/useGenerationModelFeed'
import type { GenerationModelFeedResult } from 'src/components/GenerationModel/GenerationModelList/useGenerationModelFeed'
import OrderSelect, { useOrderState } from 'src/components/OrderSelect'
import { Preloader } from 'src/components/Preloader/Preloader'
import TagGallery from 'src/components/Tag/TagGallery'
import { createTagPageUrl } from 'src/components/Tag/utils'
import { useArtworkTacksPaginationExperiment } from 'src/experiments/experiments/2604/artworkTacksPagination'
import { usePagedListArtworks } from 'src/services/apollo/artwork'
import type {
  Artwork,
  ArtworkPreviewFragment,
  ArtworkWithMangaPreviewFragment,
  GenerationModel,
  GetTackQuery,
  PageInfo,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { usePagedListGenerationModels } from 'src/services/apollo/generation-model'
import { useGetTackByCodeName } from 'src/services/apollo/tack'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { useMe } from 'src/services/helpers/user'
import {
  useTackCuratedArtworks,
  useTackCuratedArtworksInfinite,
} from 'src/services/orpc/tackCuratedArtworks'
import type { OrpcArtworkPreviewFragment } from 'src/services/orpc/utils/typeAdapters'
import { getLocale, localeAtom } from 'src/stores/locale'
import { getArtworkLink } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { createTimeRange } from 'src/utils/dayjs'
import type { Connection } from 'src/utils/graphql'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getThumbnailUrl } from 'src/utils/media'
import { createModelUrl } from 'src/utils/model'
import { getPaginationPages } from 'src/utils/pagination'
import { useQs } from 'src/utils/qs'

/** Loader-injected first-page artwork connection. */
type SSRInitialArtworkConnectionData =
  Connection<ArtworkWithMangaPreviewFragment> & {
    pageInfo?: PageInfo
    feedUuid?: string
  }

/** Loader-injected first-page model connection. */
type SSRInitialModelConnectionData = Connection<GenerationModel> & {
  pageInfo?: PageInfo
}
import { useHash } from 'src/utils/router/useHash'
import { dynamic } from 'src/utils/ssr'
import { normalizeSearch } from 'src/utils/string'
import { getLocalizedTackName } from 'src/utils/tack'
import { track } from 'src/utils/tracker'
import { useModelFilterState } from './MarketPage'

const FALLBACK_ROUTE_MAPPING = {
  artwork: '/',
  model: '/market',
} as const

const ARTWORK_MOST_LIKED_DEFAULT_CHOICES = [
  OrderOption.random,
  OrderOption.mostLiked,
  OrderOption.dailyRanking,
  OrderOption.followingNews,
  OrderOption.newest,
]

const ClientTagPageArtworkResults = dynamic(
  () => import('./TagPageArtworkResultsClient'),
  { ssr: false },
)
const ClientTagPageModelResults = dynamic(
  () => import('./TagPageModelResultsClient'),
  { ssr: false },
)

type TagPageState = {
  type: 'tack'
  // URL identifier (tag.name or tack.codeName)
  identifier: string
  // Localized display name
  displayName: string
  // API parameters
  tackId?: string // For tack mode
  // Additional metadata
  mediaId?: string
  loading?: boolean
}

type InitialTack = NonNullable<GetTackQuery['tack']>

function useTagPageState(options?: {
  /**
   * Slug supplied explicitly by the route loader (i.e. `params.tag`).
   * Used as a belt-and-braces fallback whenever `useParams()` resolves
   * empty in a given render (e.g. SSR path where the TagPage hook runs
   * under a provider tree that doesn't match the leaf route yet).
   */
  fallbackIdentifier?: string
  /**
   * DisplayName already resolved on the server (loader). Used when:
   *   - `useGetTackByCodeName` hasn't resolved yet (first SSR paint),
   *   - the resolved tack has empty `tackTerms` / `defaultName`, which
   *     would otherwise leave `localizedName = ''` and produce an empty
   *     `<h1>#</h1>` / search placeholder.
   */
  fallbackDisplayName?: string
  /**
   * Tack object already fetched on the server and injected via the
   * loader. Used as the initial value of `tackData` so that the very
   * first client render can resolve `tackId` / `identifier`
   * deterministically — instead of sitting in an
   * `identifier-but-no-tackId` state while `useGetTackByCodeName`
   * chases the Apollo cache and potentially misses on a variable
   * mismatch, which caused `<StatefulArtworkGrid>` to fire an
   * unfiltered-by-tag `ListArtworks` query (→ "No result") during
   * hydrate.
   */
  initialTack?: InitialTack | null
}) {
  // url identifier is tack.codeName
  const { tag: urlIdentifier } = useParams()
  const locale = useAtomValue(localeAtom)

  const identifier = urlIdentifier || options?.fallbackIdentifier || ''
  const fallbackDisplayName =
    options?.fallbackDisplayName?.trim() || identifier || ''

  const { data: fetchedTackData, loading } = useGetTackByCodeName(
    identifier ? identifier : skipToken,
  )

  // Prefer the freshest tack data from Apollo when available; otherwise
  // fall back to whatever the server already resolved. This makes the
  // CSR first-paint `tackId` stable even if the Apollo cache entry
  // seeded by the route loader is written under slightly different
  // variables than what `useGetTackByCodeName` emits here.
  const tackData = fetchedTackData ?? options?.initialTack ?? undefined

  // build unified state
  const tagPageState: TagPageState = useMemo(() => {
    // experiment user mode
    if (tackData) {
      // tack found
      // IMPORTANT: read locale from `useAtomValue(localeAtom)` (provider
      // store, request-scoped on the server) rather than `getLocale()`
      // (jotai default store). Under `<JotaiProvider store=...>` the
      // two stores diverge: SSR writes the request locale only into the
      // provider store, while `getLocale()` keeps reading 'en' from the
      // default store, producing tag titles like `<h1>#Snowing</h1>` on
      // the server vs `<h1>#下雪</h1>` on the client → React hydration
      // mismatch → emotion `insertBefore` exception → bail-out error
      // boundary. Closing this gap with the in-render `locale` makes
      // the localized tack name deterministic across SSR + CSR.
      const localizedName = getLocalizedTackName(
        tackData.tackTerms,
        tackData.defaultName,
        locale,
      )
      // Be defensive against partial tacks coming back from the backend.
      // If `tackData.codeName` / `tackData.id` are null or empty we would
      // otherwise hand `<StatefulArtworkGrid>` an empty `tag` AND an empty
      // `tackId`; `BaseArtworkInfiniteScroll` then strips the empty string
      // via `if (tag === 'all' || !tag) args.tag = undefined`, which ships
      // a ListArtworks query with no filter at all → every tag page ends up
      // showing the same global "Most Liked" feed after hydrate.
      const safeTackId = tackData.id || undefined
      const safeCodeName = tackData.codeName || identifier
      return {
        type: 'tack',
        identifier: safeCodeName,
        displayName: localizedName || fallbackDisplayName,
        tackId: safeTackId,
        mediaId: tackData.mediaId ?? undefined,
        loading,
      }
    } else {
      // tack not found - render empty page like unknown tag
      // don't request tag data, just use urlIdentifier
      return {
        type: 'tack',
        identifier,
        displayName: fallbackDisplayName,
        // no tackId - will result in empty results
        mediaId: undefined,
        loading,
      }
    }
  }, [tackData, identifier, fallbackDisplayName, locale, loading])

  return tagPageState
}

export const TagPage = (props: {
  type: 'model' | 'artwork'
  /**
   * Slug resolved on the server (loader `params.tag`). Passed through so
   * that SSR / first-paint renders don't have to rely on `useParams()`
   * agreeing with the route match synchronously.
   */
  initialTagSlug?: string
  /**
   * Display name resolved on the server (loader tagShell.displayName).
   * Used as a fallback until the Apollo `GetTack` query resolves.
   */
  initialDisplayName?: string
  /**
   * Tack object already fetched on the server (loader). Used to skip
   * the SSR → CSR re-query window where the ListArtworks query would
   * otherwise fire without a tackId and return "No result" for slugs
   * whose tag-name differs from the URL (e.g. "genshin_impact"
   * vs "Genshin Impact").
   */
  initialTack?: InitialTack | null
  initialArtworkConnectionData?: SSRInitialArtworkConnectionData
  initialModelConnectionData?: SSRInitialModelConnectionData
}) => {
  const { t } = useTranslation(['common', 'artwork'])

  const [{ q: queryQ, challenge: _challenge, aid }] = useQs()
  const [hash, setHash] = useHash()
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''))
  const hashQ = hashParams.get('q') ?? undefined
  const hashSort = hashParams.get('sort') ?? undefined

  // Migrate q from query string to hash
  useEffect(() => {
    if (queryQ) {
      const url = new URL(window.location.href)
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, ''),
      )
      hashParams.set('q', queryQ)
      const nextHash = hashParams.toString()
      url.searchParams.delete('q')
      url.hash = nextHash
      window.history.replaceState(null, '', url.toString())
      setHash(nextHash)
    }
  }, [queryQ, setHash])

  const q = queryQ || hashQ

  const { me } = useMe()
  const isLoggedIn = Boolean(me)
  const [filter] = useModelFilterState()

  const { isPaginated, enableExposure } = useArtworkTacksPaginationExperiment()

  const tagPageState = useTagPageState({
    fallbackIdentifier: props.initialTagSlug,
    fallbackDisplayName: props.initialDisplayName,
    initialTack: props.initialTack,
  })
  const loadingTag = tagPageState.loading

  const hasInitialArtworkResults = props.initialArtworkConnectionData != null
  const hasInitialModelResults = props.initialModelConnectionData != null
  const loading =
    loadingTag &&
    !(props.type === 'artwork'
      ? hasInitialArtworkResults
      : hasInitialModelResults)

  useEffect(() => {
    if (!loading && props.type === 'artwork') enableExposure()
  }, [loading, props.type, enableExposure])

  const [order] = useOrderState(
    props.type === 'artwork' && !isLoggedIn
      ? ARTWORK_MOST_LIKED_DEFAULT_CHOICES
      : undefined,
  )
  const challenge = +_challenge

  const keyword = typeof q === 'string' ? normalizeSearch(q) : undefined
  // Only fetch the data needed for current page type

  const { data: artworks } = usePagedListArtworks(
    !loading && props.type === 'artwork'
      ? {
          tackId: tagPageState.tackId,
          q: keyword,
          first: 1,
        }
      : skipToken,
  )
  const { data: models } = usePagedListGenerationModels(
    !loading && props.type === 'model'
      ? {
          tackId: tagPageState.tackId,
          keyword,
          first: 1,
        }
      : skipToken,
  )
  const totalCount =
    props.type === 'artwork' ? artworks?.totalCount : models?.totalCount

  // key for remounting components
  const key = useMemo(() => {
    const v = {
      identifier: tagPageState.identifier,
      q,
      aid,
      order,
      challenge,
    }
    return JSON.stringify(v)
  }, [tagPageState.identifier, q, aid, order, challenge])

  const showArtworkGrid =
    !loading &&
    props.type === 'artwork' &&
    !(isPaginated && !q && (!hashSort || hashSort === 'random'))

  // fallback route for redirection when click tag
  const fallbackRoute = FALLBACK_ROUTE_MAPPING[props.type]
  const navigateToTag = (target: string) =>
    target === tagPageState.identifier
      ? fallbackRoute
      : createTagPageUrl(props.type, target)

  return (
    <div className="justify-center items-center flex flex-col md:px-6 gap-4">
      <CustomHeroTitleWithDescription
        className="gap-1"
        titleComponent="h1"
        title={
          props.type === 'artwork'
            ? t('common:tags.artwork.hero.title', {
                tag: tagPageState.displayName,
              })
            : t('common:tags.model.hero.title', {
                tag: tagPageState.displayName,
              })
        }
        description={
          props.type === 'artwork'
            ? t('common:tags.artwork.hero.description', {
                tag: tagPageState.displayName,
              })
            : t('common:tags.model.hero.description', {
                tag: tagPageState.displayName,
              })
        }
      />
      <TagSearchBar
        type={props.type}
        tagDisplayName={tagPageState.displayName}
      />
      <TagGallery
        currentTag={tagPageState.identifier}
        navigateToTag={navigateToTag}
        navigateToContest={({ id }) => `/contest/${id}`}
        displayUnknownTag
      />
      {/* TODO: Support overriding StatefulArtworkGrid/ModelGridWrapper's loading state for better UX */}
      <OrderSelections type={props.type} isLoggedIn={isLoggedIn} />
      {loading ? (
        <Preloader />
      ) : props.type === 'artwork' ? (
        isPaginated &&
        !q &&
        (!hashSort || hashSort === 'random') &&
        !isLoggedIn ? (
          <CuratedArtworkPaginatedGrid slug={tagPageState.identifier} />
        ) : (
          // Previously wrapped in `<Suspense>`. With `useSuspense:
          // false` in i18next + no `useSuspenseQuery` /
          // `React.lazy` / `dynamic({ ssr: false })` in the grid
          // path, nothing throws server-side; the boundary only
          // served to hide the grid HTML inside a `<div hidden
          // id="S:N">` swap target, which JS-disabled crawlers
          // never reveal. Removing it lets the grid (and its real
          // `<img>` tags) inline directly into `<main>` on SSR.
          <TagArtworkGrid
            key={key}
            showArtworkGrid={showArtworkGrid}
            // Last-resort safety net: if both `tackId` and `identifier`
            // resolved empty (e.g. route params not yet matched under a
            // provider tree), fall back to the loader-provided slug
            // rather than handing ListArtworks an empty tag — otherwise
            // the feed would drop the filter entirely and return the
            // global "Most Liked" feed, making every tag page look
            // identical after hydrate.
            tag={
              tagPageState.tackId
                ? undefined
                : tagPageState.identifier || props.initialTagSlug || undefined
            }
            tackId={tagPageState.tackId}
            // Loader-provided slug feeds the oRPC `curated-artworks`
            // endpoint via `useTackCuratedArtworksInfinite` on the
            // random-sort path. Fall back to '' (empty string) when
            // unavailable so the prop type stays `string`; the hook
            // already short-circuits on falsy slug.
            slug={tagPageState.identifier || props.initialTagSlug || ''}
            q={q}
            order={order}
            challenge={challenge}
            ssrInitialConnectionData={props.initialArtworkConnectionData}
          />
        )
      ) : (
        // Same rationale as the artwork branch above: drop the
        // `<Suspense>` so `<ModelGridWrapper>`'s grid HTML inlines
        // into `<main>` instead of hiding inside a `<div hidden
        // id="S:N">` swap target gated on JS-driven `$RC`.
        <ModelGridWrapper
          key={key}
          keyAsProp={key}
          // Last-resort safety net: if tackId resolved empty, fall back
          // to the loader-provided slug so we don't drop the filter.
          tag={
            tagPageState.tackId
              ? undefined
              : tagPageState.identifier || props.initialTagSlug || undefined
          }
          tackId={tagPageState.tackId}
          keyword={q}
          ssrInitialConnectionData={props.initialModelConnectionData}
        />
      )}
    </div>
  )
}

function TagArtworkGrid({
  showArtworkGrid,
  tag,
  tackId,
  slug,
  q,
  order,
  challenge,
  ssrInitialConnectionData,
}: {
  showArtworkGrid: boolean
  tag: string | undefined
  tackId: string | undefined
  slug: string
  q: string | undefined
  order: string
  challenge: number
  ssrInitialConnectionData?: SSRInitialArtworkConnectionData
}) {
  const isRandomSort = order === OrderOption.random && !q

  const orpcFeedResult = useTackCuratedArtworksInfinite({
    slug: isRandomSort ? slug : null,
  })

  // GraphQL feed — runs for non-random sorts. Pass `tag` (HEAD-only
  // arg) so we don't drop the slug filter when the route loader only
  // resolved a slug and no `tackId`; see safety-net comment in the
  // <TagArtworkGrid> caller above.
  const fetchedGraphqlFeed = useListArtworkFeed(
    !isRandomSort && showArtworkGrid
      ? { tag, tackId, q, order, challenge }
      : null,
  )

  // Adapt SSR loader's connection-shaped seed to `ArtworkFeedResult`
  // so the grid is populated immediately on first paint without
  // waiting on the client-side Apollo cache lookup. Only used while
  // the live GraphQL query is loading and hasn't returned data —
  // once `fetchedGraphqlFeed.artworks` lands, the live result wins.
  //
  // CRUCIAL: when we adopt the SSR seed we MUST also flip
  // `loading: false` on the synthesised result. `<BaseArtworkInfiniteScroll>`
  // gates render purely on `feedResult.loading` —
  //   `const content = loadingArtworks ? renderLoading() : ... renderContent(...)`
  // — so leaving `loading: true` (carried over from `fetchedGraphqlFeed`)
  // would make it render the `<ArtworkGrid loading />` skeleton even
  // though we just handed it 24 real artwork edges, which is exactly
  // the "no images on /tags/artwork/:slug SSR" symptom we shipped
  // before this fix.
  //
  // Skipped for the random-sort branch because that path uses the
  // oRPC `useTackCuratedArtworksInfinite` feed, whose SSR seed is
  // injected into the request-scoped react-query cache by
  // `_app.tags.artwork.$tag.full-ssr.tsx` and therefore doesn't need
  // an extra adapter here.
  const graphqlFeedResult = useMemo<ArtworkFeedResult>(() => {
    if (!ssrInitialConnectionData) return fetchedGraphqlFeed
    if (!fetchedGraphqlFeed.loading || fetchedGraphqlFeed.artworks.length > 0) {
      return fetchedGraphqlFeed
    }
    const initialArtworks = getNodesFromConnection(
      ssrInitialConnectionData,
    ) as ArtworkFeedResult['artworks']
    if (initialArtworks.length === 0) return fetchedGraphqlFeed
    return {
      ...fetchedGraphqlFeed,
      artworks: initialArtworks,
      loading: false,
      feedUuid:
        ssrInitialConnectionData.feedUuid ?? fetchedGraphqlFeed.feedUuid,
    }
  }, [fetchedGraphqlFeed, ssrInitialConnectionData])

  return (
    <StatefulArtworkGrid
      feedResult={isRandomSort ? orpcFeedResult : graphqlFeedResult}
      q={q}
      order={order}
      trackingArgs={{ feed: order, scene: 'tags' }}
    />
  )
}

function usePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const page = Math.max(1, Number(searchParams.get('page')) || 1)

  const setPage = useCallback(
    (p: number) => {
      const next = new URLSearchParams(searchParams)
      if (p <= 1) {
        next.delete('page')
      } else {
        next.set('page', String(p))
      }
      const search = next.toString()
      navigate((search ? `?${search}` : '') + window.location.hash, {
        preventScrollReset: true,
      })
    },
    [navigate, searchParams],
  )

  return { page, setPage }
}

function CuratedArtworkPaginatedGrid({ slug }: { slug: string }) {
  const { t } = useTranslation(['action'])
  const { page, setPage } = usePage()
  const { data, isLoading } = useTackCuratedArtworks(slug, page)
  const { me } = useMe()
  const isLoggedIn = Boolean(me)
  const navigate = useNavigate()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [detailArtwork, setDetailArtwork] = useState<ArtworkPreviewFragment>()

  const openArtwork = useCallback(
    (artwork: OrpcArtworkPreviewFragment) => {
      if (!isLoggedIn) {
        navigate(getArtworkLink(artwork))
      } else {
        setDetailArtwork(artwork)
        setIsDetailOpen(true)
      }
    },
    [isLoggedIn, navigate],
  )

  useEffect(() => {
    if (!data) return
    if (data.totalPages > 0 && page > data.totalPages) {
      setPage(data.totalPages)
    }
  }, [data, page, setPage])

  if (isLoading) return <Preloader />

  const items = data?.data ?? []
  const totalPages = data?.totalPages ?? 1
  const hasNextPage = page < totalPages
  const paginationPages = getPaginationPages(page, totalPages)

  return (
    <>
      <div className="w-full flex flex-col gap-4">
        <div className="w-full grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3">
          {items.map(item => (
            <CuratedArtworkCard
              key={item.id}
              item={item}
              onOpen={openArtwork}
            />
          ))}
        </div>
        {items.length > 0 && (
          <div className="flex justify-center m-8">
            <ButtonGroup>
              <Button
                variant="outlined"
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="w-24"
                startIcon={<IcBaselineChevronLeft className="size-6" />}
              >
                <span className="capitalize">{t('action:previous')}</span>
              </Button>
              {paginationPages.map((p, idx) =>
                p === 'ellipsis' ? (
                  <Button
                    key={`ellipsis-${idx}`}
                    variant="outlined"
                    size="small"
                    disabled
                    className="min-w-0 w-10"
                  >
                    …
                  </Button>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setPage(p)}
                    className="min-w-0 w-10"
                  >
                    {p}
                  </Button>
                ),
              )}
              <Button
                variant="outlined"
                size="small"
                disabled={!hasNextPage}
                onClick={() => setPage(page + 1)}
                className="w-24"
                endIcon={<IcBaselineChevronRight className="size-6" />}
              >
                <span className="capitalize">{t('action:next')}</span>
              </Button>
            </ButtonGroup>
          </div>
        )}
      </div>
      <ArtworkDetailDialog
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        artwork={detailArtwork}
      />
    </>
  )
}

export const Component = TagPage

function OrderSelections(props: {
  type: 'artwork' | 'model'
  isLoggedIn: boolean
}) {
  const [order, setOrder] = useOrderState(
    props.type === 'artwork' && !props.isLoggedIn
      ? ARTWORK_MOST_LIKED_DEFAULT_CHOICES
      : undefined,
  )
  const [filter, setFilter] = useModelFilterState()
  const { type } = props
  const { t } = useTranslation(['common', 'artwork', 'market'])

  const commonOrderChoices = [
    {
      value: OrderOption.mostLiked,
      label: t('common:order-option.most-liked'),
    },
    { value: OrderOption.newest, label: t('common:order-option.newest') },
  ]

  const orderChoicesForArtworks = !props.isLoggedIn
    ? [
        {
          value: OrderOption.random,
          label: t('common:order-option.random'),
        },
        {
          value: OrderOption.mostLiked,
          label: t('common:order-option.most-liked'),
        },
        {
          label: t('common:order-option.daily-ranking'),
          value: OrderOption.dailyRanking,
        },
        { value: OrderOption.newest, label: t('common:order-option.newest') },
      ]
    : [
        {
          value: OrderOption.random,
          label: t('common:order-option.random'),
        },
        {
          label: t('common:order-option.daily-ranking'),
          value: OrderOption.dailyRanking,
        },
        ...commonOrderChoices,
      ]

  const orderChoicesForModels = [
    {
      value: OrderOption.trending,
      label: t('common:order-option.trending'),
    },
    { value: OrderOption.mostUsed, label: t('common:order-option.most-used') },
    ...commonOrderChoices,
  ]

  const modelChoices = [
    {
      value: 'all',
      label: t('market:search.all-categories'),
    },
    {
      value: GenerationModelType.Lora,
      label: t('market:model.type.lora'),
    },
    {
      value: GenerationModelType.SdV1Model,
      label: t('market:model.type.sdv1model'),
    },
  ]

  const dateRangeChoices = [
    {
      value: 'all',
      label: t('market:search.all-time'),
    },
    {
      value: 'today',
      label: t('market:search.today'),
    },
    {
      value: 'this-week',
      label: t('market:search.this-week'),
    },
    {
      value: 'this-month',
      label: t('market:search.this-month'),
    },
  ]

  if (type === 'artwork') {
    return (
      <div className="w-full min-w-0 self-start">
        <OrderSelect
          choices={orderChoicesForArtworks}
          value={order}
          onChange={v => {
            track('order_click', { value: v, scene: 'tags' })
            return setOrder(v)
          }}
          className="md:hidden"
          alwaysCollapse
        />
        <OrderSelect
          choices={orderChoicesForArtworks}
          value={order}
          onChange={v => {
            track('order_click', { value: v, scene: 'tags' })
            return setOrder(v)
          }}
          className="hidden md:block"
        />
      </div>
    )
  }
  return (
    <div className="relative mt-2 flex gap-2 overflow-auto w-full self-start">
      <OrderSelect
        choices={orderChoicesForModels}
        onChange={v => {
          track('order_click', {
            value: v,
            scene: 'tags',
          })
          setFilter({ order: v })
        }}
        value={filter.order}
        alwaysCollapse
      />
      <OrderSelect
        choices={modelChoices}
        onChange={v => {
          track('cate_click', { cate: v, scene: 'tags' })
          setFilter({ type: [v] })
        }}
        value={filter.type || 'all'}
        alwaysCollapse
      />
      <OrderSelect
        choices={dateRangeChoices}
        onChange={v => {
          track('time_select', { time: v, scene: 'tags' })
          setFilter({ timeRange: v })
        }}
        value={filter.timeRange || 'all'}
        alwaysCollapse
      />
    </div>
  )
}

function ModelGridWrapper(props: {
  keyAsProp: string
  keyword?: string
  tag?: string
  tackId?: string
  ssrInitialConnectionData?: SSRInitialModelConnectionData
}) {
  const {
    keyAsProp: key,
    keyword,
    tag,
    tackId,
    ssrInitialConnectionData,
  } = props
  const [{ type, timeRange, order }] = useModelFilterState()
  const fetchedFeed = useGenerationModelFeed({
    order,
    keyword,
    tag,
    tackId,
    type: type === 'all' ? undefined : type,
    timeRange: createTimeRange(timeRange ?? ''),
  })

  // Adapt SSR loader's connection-shaped seed to GenerationModelFeedResult
  // so the grid is populated immediately on first paint without waiting
  // on the client-side feed hook. Once `fetchedFeed.models` lands, the
  // live result wins.
  //
  // CRUCIAL: flip `loading: false` on the synthesised result —
  // `<BaseModelListInfiniteScroll>` gates render purely on
  // `feedResult.loading` (`loading ? renderLoading() :
  // renderContent(...)`). Without this override we'd render the
  // skeleton branch even though `feedResult.models` already has
  // real loader-seeded entries, which strips every `<img>` URL
  // out of the SSR `<main>` on `/zh/tags/model/<tag>`.
  const feedResult = useMemo<GenerationModelFeedResult>(() => {
    if (!ssrInitialConnectionData) return fetchedFeed
    if (!fetchedFeed.loading || fetchedFeed.models.length > 0) {
      return fetchedFeed
    }
    const initialModels = getNodesFromConnection(
      ssrInitialConnectionData,
    ) as GenerationModelFeedResult['models']
    if (initialModels.length === 0) return fetchedFeed
    return { ...fetchedFeed, models: initialModels, loading: false }
  }, [fetchedFeed, ssrInitialConnectionData])

  return (
    <BaseModelListInfiniteScroll
      key={key}
      feedResult={feedResult}
      renderContent={p => <ModelGrid scene="tags" {...p} />}
      renderLoading={() => <ModelGrid loading />}
    />
  )
}

function TagSearchBar(props: {
  type: 'artwork' | 'model'
  className?: string
  tagDisplayName?: string
}) {
  const { tag } = useParams()
  const { t } = useTranslation(['common', 'market'])
  const [{ q: queryQ }] = useQs()
  const [hash, setHash] = useHash()
  const hashQ = new URLSearchParams(hash.replace(/^#/, '')).get('q') ?? ''
  const [keyword, setKeyword] = useState(queryQ || hashQ)

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault()
    const hashParams = new URLSearchParams(hash.replace(/^#/, ''))
    if (keyword) {
      hashParams.set('q', keyword)
    } else {
      hashParams.delete('q')
    }
    hashParams.set('sort', 'random')
    setHash(hashParams.toString())
    e.currentTarget.querySelector('input')?.blur()
    track('search', { scene: 'tags', value: keyword, tag })
  }

  // Use provided display name or fall back to URL param
  const displayName = props.tagDisplayName || tag

  return (
    <form
      noValidate
      action=""
      onSubmit={onSubmit}
      className={cx(
        'flex w-full justify-center items-center gap-4',
        props.className,
      )}
    >
      <OutlinedInput
        className="grow rounded-2xl"
        startAdornment={
          <InputAdornment position="start">
            <IcBaselineSearch className="size-6" />
          </InputAdornment>
        }
        endAdornment={
          keyword && (
            <InputAdornment position="end">
              <IconButton
                type="button"
                onClick={() => {
                  setKeyword('')
                }}
              >
                <IcBaselineClear className="size-6" />
              </IconButton>
            </InputAdornment>
          )
        }
        value={keyword}
        onChange={e => setKeyword(e.target.value)}
        inputProps={{
          enterKeyHint: 'search',
        }}
        placeholder={
          props.type === 'artwork'
            ? t('common:tags.artwork.search-placeholder', { tag: displayName })
            : t('common:tags.model.search-placeholder', { tag: displayName })
        }
      />
      <input type="submit" className="hidden" />
    </form>
  )
}
